// frontend/src/App.js
// Main application container with Redux authentication and role-based routing
// This is the main React app shell, loading the current user and wiring up role-based routing (UPS vs Agency), protected dashboards, auth pages, and the shared header/layout.

import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// socket bootstrap import
import { initSocket } from './services/socket';

// Global components
import Header from './components/Header';

// Auth pages
import Login from './features/auth/Login';
import ForgotPassword from './features/auth/ForgotPassword';
import ResetPassword from './features/auth/ResetPassword';

// UPS admin layout and pages
import UpsDashboard from './features/ups/UpsDashboard';
import OrganizedDriverList from './features/ups/OrganizedDriverList';
import UpsReservedDriverList from './features/ups/UpsReservedDriverList';
import UpsRequestsDriverList from './features/ups/UpsRequestsDriverList';
import BannedDriversList from './features/ups/BannedDriversList';
import UpsBlockedAssignmentsList from './features/ups/UpsBlockedAssignmentsList';
import AccountDashboard from './features/ups/AccountDashboard';
import ManageAgencies from './features/ups/ManageAgencies';
import ManageSites from './features/ups/ManageSites';
import ManageInternalDrivers from './features/ups/ManageInternalDrivers';



// site loading
import {
  loadMyCompanySites,
  loadMyAgencyAllowedSites,
} from './features/sites/sitesSlice';

// Agency layout and pages
import AgencyDashboard from './features/agency/AgencyDashboard';
import AgencyActiveDriverList from './features/agency/AgencyActiveDriverList';
import AgencyReservedDriverList from './features/agency/AgencyReservedDriverList';
import AgencyAvailableDriverList from './features/agency/AgencyAvailableDriverList';
import AgencyBannedDriverList from './features/agency/AgencyBannedDriverList';

// Auth actions
import { fetchCurrentUser } from './features/auth/authSlice';

// Route-aware app shell:
// - Lives inside <Router> so it can safely use useLocation()
// - Decides when to show the shared global Header
// - Owns the full route tree for UPS / Agency / public auth pages
function AppShell({ isLoggedIn, role }) {
  const location = useLocation();

  // UPS control-center routes own their own shell/header.
  // Hide the global Header on /ups-admin/* so UpsDashboard can render
  // the dedicated UPS platform chrome instead.
  const isUpsPlatformRoute =
    location.pathname === '/ups-admin' ||
    location.pathname.startsWith('/ups-admin/');

  return (
    <>
      {/* Show global header only outside the UPS platform shell */}
      {isLoggedIn && !isUpsPlatformRoute && <Header />}

      <Routes>
        {/* UPS Admin Dashboard and Routes */}
        <Route
          path="/ups-admin/*"
          element={
            isLoggedIn && ['ups_admin', 'ups_user'].includes(role)
              ? <UpsDashboard />
              : <Navigate to="/" replace />
          }
        >
          <Route path="drivers/">
            <Route index element={<OrganizedDriverList />} />
            <Route path="active" element={<OrganizedDriverList />} />
            <Route path="requests" element={<UpsRequestsDriverList />} />
            <Route path="reserved" element={<UpsReservedDriverList />} />
            <Route path="banned" element={<BannedDriversList />} />
            <Route path="blocked" element={<UpsBlockedAssignmentsList />} />
          </Route>

          <Route path="accounts" element={<AccountDashboard />} />
          <Route path="manage-agencies" element={<ManageAgencies />} />
          <Route path="manage-sites" element={<ManageSites />} />
          <Route path="manage-internal-drivers" element={<ManageInternalDrivers />} />
          <Route path="*" element={<Navigate to="/ups-admin/drivers" replace />} />
        </Route>

        {/* Agency Dashboard and Routes */}
        <Route
          path="/agency/*"
          element={
            isLoggedIn && role?.startsWith('agency')
              ? <AgencyDashboard />
              : <Navigate to="/" replace />
          }
        >
          <Route index element={<Navigate to="drivers" replace />} />
          <Route path="drivers" element={<AgencyActiveDriverList />} />
          <Route path="reserved" element={<AgencyReservedDriverList />} />
          <Route path="available" element={<AgencyAvailableDriverList />} />
          <Route path="banned" element={<AgencyBannedDriverList />} />
          <Route path="*" element={<Navigate to="drivers" replace />} />
        </Route>

        {/* Public / Auth Routes
            - Non-UPS routes
            - Public auth pages
            - Root redirects
            - Routes that still use the shared/global app chrome */}
        {/* Public / Auth Routes */}
        <Route element={<DefaultLayout />}>
          <Route
            path="/login"
            element={!isLoggedIn ? <Login /> : <Navigate to="/" replace />}
          />
          <Route
            path="/forgot-password"
            element={!isLoggedIn ? <ForgotPassword /> : <Navigate to="/" replace />}
          />
          <Route
            path="/reset-password"
            element={!isLoggedIn ? <ResetPassword /> : <Navigate to="/" replace />}
          />

          <Route
            path="/"
            element={
              isLoggedIn
                ? role?.startsWith('agency')
                  ? <Navigate to="/agency/drivers" replace />
                  : <Navigate to="/ups-admin/drivers" replace />
                : <Navigate to="/login" replace />
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

// Layout for public/default routes
function DefaultLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

// App bootstrap layer:
// - Restores auth session
// - Initializes socket after auth is known
// - Preloads role-scoped site data
// - Mounts Router, then hands rendering to AppShell
function App() {
  const dispatch = useDispatch();
  const { userInfo, loading } = useSelector(state => state.auth);

  // 1) restore session
  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch]);

  // 2) init socket after auth is known
  useEffect(() => {
    if (!userInfo) return;
    // ✅ connect socket only once auth is known
    initSocket();
  }, [userInfo]);

  // 3) role-aware site loading (MUST be before any early returns)
  useEffect(() => {
    if (!userInfo) return;

    const role = userInfo.role;

    if (role === 'agency_admin' || role === 'agency_user') {
      dispatch(loadMyAgencyAllowedSites());
    } else if (role === 'ups_admin' || role === 'ups_user') {
      dispatch(loadMyCompanySites());
    }
  }, [dispatch, userInfo]);

  // ✅ now early returns are safe
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  const isLoggedIn = Boolean(userInfo);
  const role = userInfo?.role;

  // AppShell must render inside <Router> because it uses useLocation()
  // for route-aware layout decisions (e.g. hiding the global Header on /ups-admin/*).
  return (
    <Router>
      <AppShell isLoggedIn={isLoggedIn} role={role} />
    </Router>
  );
}

export default App;
